import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import PriorityHighOutlinedIcon from '@mui/icons-material/PriorityHighOutlined';
import { Snackbar, SxProps, Theme, Typography } from '@mui/material';
import { clsx } from 'clsx';
import React, { memo, useCallback, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '~/store/hooks';
import { setOption } from '~/store/slices/notification';
import { selectOption } from '~/store/slices/notification/selectors';

import styles from './styles.module.scss';

const ICON_DEFAULT_STYLES: SxProps<Theme> = {
  width: '15px',
  height: '15px',
  color: 'var(--white-color)',
};

export interface NotificationOption {
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  description?: string;
}

export const Component: React.FC = () => {
  const dispatch = useAppDispatch();
  const option = useAppSelector(selectOption);

  const handleClose = useCallback(() => {
    dispatch(setOption(null));
  }, [dispatch]);

  const icon = useMemo(() => {
    if (option?.type === 'success') {
      return <CheckIcon sx={ICON_DEFAULT_STYLES} />;
    }

    if (option?.type === 'error') {
      return <CloseIcon sx={ICON_DEFAULT_STYLES} />;
    }

    if (option?.type === 'warning') {
      return <PriorityHighOutlinedIcon sx={ICON_DEFAULT_STYLES} />;
    }

    if (option?.type === 'info') {
      return (
        <PriorityHighOutlinedIcon
          sx={{
            ...ICON_DEFAULT_STYLES,
            transform: 'rotate(180deg)',
          }}
        />
      );
    }

    return null;
  }, [option?.type]);

  return (
    <Snackbar
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      autoHideDuration={5000}
      open={!!option}
      onClose={handleClose}
    >
      {option ? (
        <div className={clsx(styles.notification, styles[`notification--${option.type}`])}>
          <div
            className={clsx(
              styles.notification__icon,
              styles[`notification__icon--${option.type}`],
            )}
          >
            {icon}
          </div>
          <div className={styles.notification__content}>
            <Typography
              color="var(--text-4-color)"
              fontWeight="500"
              lineHeight="24px"
              variant="subtitle1"
            >
              {option.title}
            </Typography>
            <Typography color="var(--text-4-color)" lineHeight="18px" variant="subtitle2">
              {option.message}
            </Typography>
            {option.description && (
              <Typography color="var(--gray-2-color)" lineHeight="18px" variant="subtitle2">
                {option.description}
              </Typography>
            )}
          </div>
        </div>
      ) : (
        <div />
      )}
    </Snackbar>
  );
};

export const Notification = memo(Component);
